import React, { useEffect, useState } from 'react';
import { IonApp, setupIonicReact } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { Route, Redirect, Switch } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { PushNotifications } from '@capacitor/push-notifications';
import { useAuthStore } from './store/authStore';
// Profile selector removed — users go straight to app after login
import { api } from './services/api';
import { requestWebPushToken, onForegroundMessage } from './services/firebase';
import './store/themeStore'; // Initialize theme on app load

import LandingPage from './pages/Landing/LandingPage';
import FeaturesPage from './pages/Features/FeaturesPage';
import LoginPage from './pages/Auth/LoginPage';
import RegisterPage from './pages/Auth/RegisterPage';
import ForgotPasswordPage from './pages/Auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/Auth/ResetPasswordPage';
import VerifyEmailPage from './pages/Auth/VerifyEmailPage';
import FamilySetupPage from './pages/Family/FamilySetupPage';
import TabsLayout from './components/TabsLayout';
import ToastContainer from './components/Toast';
import ErrorBoundary from './components/ErrorBoundary';
import CookieBanner from './components/CookieBanner';
import PrivacyPolicyPage from './pages/Legal/PrivacyPolicyPage';
import TermsOfServicePage from './pages/Legal/TermsOfServicePage';

// Apple devices get Ionic's iOS mode — native slide/parallax page transitions
// and iOS-styled components — while Android/desktop keep Material. Covers the
// native iOS build, iPhone/iPad Safari, and the installed iOS PWA (where
// Capacitor.getPlatform() reports 'web', so we also sniff the user agent).
// iPadOS 13+ reports as 'MacIntel' with touch points, hence the extra check.
const isApplePlatform = (): boolean => {
  try {
    if (Capacitor.getPlatform() === 'ios') return true;
    const ua = navigator.userAgent || '';
    if (/iPad|iPhone|iPod/.test(ua)) return true;
    if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true;
  } catch {
    /* SSR / no navigator — fall through to Material */
  }
  return false;
};

setupIonicReact({
  mode: isApplePlatform() ? 'ios' : 'md',
  animated: true,
});

const CHUNK_RELOAD_GUARD_KEY = 'fh-chunk-reload-attempted';

const SPLASH_MAX_MS = 2500;

const clearChunkCaches = async () => {
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch {
    // Ignore cache clearing issues and still attempt reload.
  }
};

const recoverFromChunkError = async () => {
  // Prevent infinite reload loop if the deployed bundle is broken.
  if (sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY) === '1') return;
  sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, '1');
  await clearChunkCaches();
  window.location.reload();
};

// Bug #49: Handle ChunkLoadError by forcing a clean reload once
if (typeof window !== 'undefined') {
  window.addEventListener('error', (e) => {
    if (e.message?.includes('Loading chunk') || e.message?.includes('ChunkLoadError')) {
      void recoverFromChunkError();
    }
  });
}

const isPWA = () => {
  return window.matchMedia('(display-mode: standalone)').matches
    || (window.navigator as any).standalone === true;
};

// Splash — Variant 2 "Hearth Rise" from the design prototype:
// dark ink ground, logo drops in from above with back-ease, wordmark
// letters cascade (first 3 terra, rest cream), Instrument Serif italic
// tagline.
const LoadingScreen: React.FC = () => (
  <div className="fh-splash fh-splash--v2">
    <img
      src="/icon-512-nobg.png"
      alt="Famhearth"
      className="fh-splash__logo-drop"
      width={180}
      height={180}
    />
    <div className="fh-splash__wordmark-v2">
      {'Famhearth'.split('').map((ch, i) => (
        <span key={i} className="fh-splash__letter" style={{ animationDelay: `${1.4 + i * 0.08}s`, color: i < 3 ? '#c5512d' : '#f5f3ee' }}>
          {ch}
        </span>
      ))}
    </div>
    <div className="fh-splash__tag">One home. One hearth.</div>
  </div>
);

const App: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading, initAuth, setFcmToken, family } = useAuthStore();
  const [splashDelayElapsed, setSplashDelayElapsed] = useState(false);

  useEffect(() => {
    // New successful app boot -> clear one-shot chunk reload guard.
    sessionStorage.removeItem(CHUNK_RELOAD_GUARD_KEY);
    initAuth();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      if (Capacitor.isNativePlatform()) {
        registerPushNotifications();
      } else {
        registerWebPush();
      }
    }
  }, [isAuthenticated]);

  const registerPushNotifications = async () => {
    try {
      let permStatus = await PushNotifications.checkPermissions();
      if (permStatus.receive === 'prompt') {
        permStatus = await PushNotifications.requestPermissions();
      }
      if (permStatus.receive !== 'granted') return;
      await PushNotifications.register();
      PushNotifications.addListener('registration', async (token) => {
        await api.put('/auth/fcm-token', { token: token.value, platform: Capacitor.getPlatform() });
        setFcmToken(token.value);
      });
    } catch (error) {
      console.error('Push notification setup error:', error);
    }
  };

  const registerWebPush = async () => {
    try {
      const token = await requestWebPushToken();
      if (token) {
        setFcmToken(token);
        onForegroundMessage((payload) => {
          // Foreground FCM message received — handled by notification service
        });
      }
    } catch (error) {
      console.info('Web push not available:', error);
    }
  };


  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const setupNativeUi = async () => {
      try {
        await StatusBar.setStyle({ style: Style.Dark });
        await StatusBar.setBackgroundColor({ color: '#0f1923' });
      } catch {
        // Ignore status bar setup failures.
      }
    };

    const backHandlerPromise = CapacitorApp.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        CapacitorApp.exitApp();
      }
    });

    void setupNativeUi();

    return () => {
      void backHandlerPromise.then((handle) => handle.remove());
    };
  }, []);


  // Keep a global keyboard-height CSS var in sync with visualViewport.
  // We use this for scrollable form bodies (extra bottom space) while
  // keeping fixed CTAs pinned to safe-area insets.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const update = () => {
      const offset = Math.max(
        0,
        window.innerHeight - vv.height - vv.offsetTop,
      );
      const keyboardHeight = offset > 150 ? offset : 0;
      document.body.classList.toggle('fh-keyboard-open', keyboardHeight > 0);
      document.documentElement.style.setProperty('--fh-keyboard-height', `${keyboardHeight}px`);
    };

    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);

    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      document.body.classList.remove('fh-keyboard-open');
      document.documentElement.style.setProperty('--fh-keyboard-height', '0px');
    };
  }, []);

  useEffect(() => {
    if (!authLoading) {
      setSplashDelayElapsed(true);
      return;
    }

    const timer = window.setTimeout(() => setSplashDelayElapsed(true), SPLASH_MAX_MS);
    return () => window.clearTimeout(timer);
  }, [authLoading]);

  // Show loading screen while auth is initializing to prevent deep-link blank screen
  if (authLoading && !splashDelayElapsed) {
    return <IonApp><LoadingScreen /></IonApp>;
  }

  return (
    <IonApp>
      <ToastContainer />
      <CookieBanner />
      <ErrorBoundary>
      <IonReactRouter>
        <Switch>
          <Route path="/login" component={LoginPage} exact />
          <Route path="/register" component={RegisterPage} exact />
          <Route path="/forgot-password" component={ForgotPasswordPage} exact />
          <Route path="/reset-password" component={ResetPasswordPage} exact />
          <Route path="/verify-email" component={VerifyEmailPage} exact />
          <Route path="/family-setup" component={FamilySetupPage} exact />
          <Route path="/features" component={FeaturesPage} exact />
          <Route path="/privacy" component={PrivacyPolicyPage} exact />
          <Route path="/terms" component={TermsOfServicePage} exact />
          <Redirect exact from="/welcome" to="/login" />
          <Redirect exact from="/profiles" to="/app/home" />
          <Route path="/app" render={() =>
            isAuthenticated ? <TabsLayout /> : <Redirect to="/login" />
          } />
          <Route exact path="/" render={() => {
            if (isAuthenticated) return <Redirect to={family ? '/app/home' : '/family-setup'} />;
            if (Capacitor.isNativePlatform() || isPWA()) return <Redirect to="/login" />;
            return <LandingPage />;
          }} />
        </Switch>
      </IonReactRouter>
      </ErrorBoundary>
    </IonApp>
  );
};

export default App;
